import React, { useState } from 'react';
import { Button, Menu, Modal } from 'antd';

import style from './Sidebar.module.scss';
import api from '../../../helpers/api';

const GARANT_TOKEN_COOKIE = 'garant_checkdoc_token';
const GARANT_SERVICE_URL = 'https://checkdoc-tektorg.garant.ru/';
const TOKEN_TTL_SECONDS = 8 * 60 * 60;

const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
};

const saveToken = (token: string) => {
  document.cookie = [
    `${GARANT_TOKEN_COOKIE}=${encodeURIComponent(token)}`,
    `Max-Age=${TOKEN_TTL_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
    'Secure',
  ].join('; ');
};

const isTokenAlive = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return !payload.exp || payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

const getErrorText = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    return data.message || data.error || `Ошибка сервиса: ${response.status}`;
  } catch {
    return `Ошибка сервиса: ${response.status}`;
  }
};

const openGarant = (token: string, openedTab?: Window | null) => {
  const url = new URL(GARANT_SERVICE_URL);
  url.searchParams.set('token', token);

  const targetTab = openedTab;

  if (targetTab) {
    targetTab.location.href = url.toString();
    return;
  }

  window.open(url.toString(), '_blank', 'noopener,noreferrer');
};

const GarantCheckdocMenuItem: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setError(null);

    const savedToken = getCookie(GARANT_TOKEN_COOKIE);

    if (savedToken && isTokenAlive(savedToken)) {
      openGarant(savedToken);
      return;
    }

    // Вкладка создаётся во время пользовательского клика,
    // поэтому не будет заблокирована браузером после await.
    const garantTab = window.open('', '_blank');
    setIsLoading(true);

    try {
      const response = await api.getGarantToken();

      if (!response.ok) {
        throw new Error(await getErrorText(response));
      }

      const token = (await response.text()).trim();

      if (!token) {
        throw new Error('Сервис вернул пустой токен авторизации');
      }

      saveToken(token);
      openGarant(token, garantTab);
    } catch (requestError) {
      garantTab?.close();

      setError(requestError instanceof Error ? requestError.message : 'Не удалось получить токен авторизации');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Menu.Item key="garant-checkdoc" disabled={isLoading}>
        <button type="button" className={`${style.navLink} ${style.case}`} onClick={handleClick}>
          Проверка закупочной документации
        </button>
      </Menu.Item>

      <Modal
        visible={Boolean(error)}
        title="Ошибка"
        closable
        destroyOnClose
        maskClosable={false}
        onCancel={() => setError(null)}
        footer={[
          <Button key="retry" type="primary" loading={isLoading} onClick={handleClick}>
            Повторить попытку
          </Button>,
        ]}
      >
        <p>
          Произошла непредвиденная ошибка: {error}.
          <br />
          Если ошибка повторяется — свяжитесь со специалистами технической поддержки.
        </p>
      </Modal>
    </>
  );
};

export default GarantCheckdocMenuItem;
